import React, { useEffect, useState } from "react";
import { getGoLive, tickGoLive, syncStripeCatalog, setPlatformTax } from "@/lib/apiClient";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { CheckCircle2, Circle, AlertTriangle, HelpCircle, Loader2, RefreshCw, Copy, Rocket } from "lucide-react";

const ICON = {
  done: { I: CheckCircle2, cls: "text-emerald-500" },
  partial: { I: AlertTriangle, cls: "text-amber-500" },
  todo: { I: Circle, cls: "text-muted-foreground/60" },
  unknown: { I: HelpCircle, cls: "text-muted-foreground/60" },
};

const Item = ({ it, onTick, onSync, busy }) => {
  const { I, cls } = ICON[it.status] || ICON.todo;
  const copy = () => { navigator.clipboard.writeText(it.copy); toast.success("Copied"); };
  return (
    <li className={cn("flex gap-3 rounded-lg border p-3", it.status === "done" ? "border-emerald-500/30 bg-emerald-500/5" : "border-border bg-card")} data-testid={`golive-item-${it.key}`} data-status={it.status}>
      <I className={cn("h-5 w-5 shrink-0 mt-0.5", cls)} />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className={cn("text-sm font-medium", it.status === "done" && "line-through decoration-emerald-500/60")}>{it.title}</span>
          {it.manual && (
            <label className="ml-auto flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer">
              <Checkbox data-testid={`golive-tick-${it.key}`} checked={!!it.done_at} disabled={busy} onCheckedChange={(v) => onTick(it.key, !!v)} /> I've done this
            </label>
          )}
        </div>
        <p className="text-xs text-muted-foreground mt-0.5" data-testid={`golive-detail-${it.key}`}>{it.detail}</p>
        {it.hint && it.status !== "done" && <p className="text-xs text-muted-foreground/80 mt-1">{it.hint}</p>}
        {it.key === "stripe_catalog" && it.status !== "done" && (
          <Button size="sm" variant="outline" className="mt-2 h-7 text-xs" onClick={onSync} disabled={busy} data-testid="golive-sync-catalog">{busy ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <RefreshCw className="h-3 w-3 mr-1" />} Sync catalogue to Stripe</Button>
        )}
        {it.copy && it.status !== "done" && (
          <div className="mt-1.5 flex flex-wrap items-center gap-2">
            <code className="rounded bg-background/60 border border-border px-2 py-0.5 text-[11px] font-mono break-all">{it.copy}</code>
            <Button size="sm" variant="outline" className="h-6 px-2 text-[11px]" onClick={copy} data-testid={`golive-copy-${it.key}`}><Copy className="h-3 w-3 mr-1" /> Copy</Button>
          </div>
        )}
      </div>
    </li>
  );
};

export const GoLiveChecklist = () => {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => { setBusy(true); return getGoLive().then(setD).catch(() => setD(false)).finally(() => setBusy(false)); };
  useEffect(() => { load(); }, []);
  const onSync = () => {
    setBusy(true);
    syncStripeCatalog().then((r) => { setD(r.go_live); toast.success(`Catalogue synced — ${r.catalog.present}/${r.catalog.expected} prices in ${r.catalog.mode} mode`); })
      .catch((e) => toast.error(e?.response?.data?.detail || "Sync failed")).finally(() => setBusy(false));
  };
  const onVat = (v) => {
    setBusy(true);
    setPlatformTax(v).then((r) => { setD(r); toast.success(v ? "VAT registered — prices now show “incl. VAT”" : "Not VAT registered — VAT wording removed (prices unchanged)"); })
      .catch(() => toast.error("Could not save")).finally(() => setBusy(false));
  };
  const onTick = (key, done) => { setBusy(true); tickGoLive(key, done).then(setD).catch(() => toast.error("Could not save")).finally(() => setBusy(false)); };
  if (d === null) return <div className="flex justify-center py-16"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>;
  if (d === false) return <p className="text-sm text-red-500" data-testid="golive-error">Could not load the go-live checklist.</p>;
  const pct = Math.round((d.done / d.total) * 100);
  return (
    <section className="space-y-4" data-testid="golive-checklist">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-base md:text-lg font-semibold flex items-center gap-2"><Rocket className="h-4 w-4 text-primary" /> Go-live checklist</h2>
          <p className="text-sm text-muted-foreground">Auto-detected from the running deployment{d.public_base_url ? ` (${d.public_base_url})` : ""}. Only you can see this.</p>
        </div>
        <Button size="sm" variant="ghost" onClick={load} disabled={busy} data-testid="golive-refresh"><RefreshCw className={cn("h-3.5 w-3.5 mr-1", busy && "animate-spin")} /> Re-check</Button>
      </div>
      <div className="rounded-xl border border-border bg-card p-4" data-testid="golive-progress">
        <div className="flex items-center justify-between text-sm"><span className={cn("font-semibold", d.ready ? "text-emerald-500" : "text-foreground")}>{d.ready ? "All set — you're live" : `${d.done} of ${d.total} complete`}</span><span className="font-mono text-xs text-muted-foreground">{pct}%</span></div>
        <div className="mt-2 h-2 rounded-full bg-muted overflow-hidden"><div className={cn("h-full rounded-full transition-all", d.ready ? "bg-emerald-500" : "bg-primary")} style={{ width: `${pct}%` }} /></div>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border bg-card p-4" data-testid="golive-vat-toggle">
        <div>
          <div className="text-sm font-medium">Are you VAT registered?</div>
          <p className="text-xs text-muted-foreground mt-0.5">{d.vat_registered ? "Prices show “incl. VAT” and VAT is carved out of them via Stripe Tax." : "No VAT wording is shown anywhere. Prices stay exactly the same when you switch this on later."}</p>
        </div>
        <label className="flex items-center gap-2 text-sm"><span className="text-muted-foreground">{d.vat_registered ? "Yes" : "No"}</span><Switch data-testid="golive-vat-switch" checked={!!d.vat_registered} disabled={busy} onCheckedChange={onVat} /></label>
      </div>
      <ul className="space-y-2">{d.items.map((it) => <Item key={it.key} it={it} onTick={onTick} onSync={onSync} busy={busy} />)}</ul>
    </section>
  );
};
